import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ActivityIndicator, FlatList, Image, Modal, ScrollView, StyleSheet, Text, TouchableOpacity, View } from './rn';
import PageHeader from './PageHeader';
import PrimaryActionButton from './PrimaryActionButton';
import { api } from '../services/api';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import { formatDisplayCount } from '../i18n/formatCount';
import { catalogArchive, keyOf, type ArchiveCatalog, type ArchiveCategory, type ArchiveMedia } from '../instagramArchive/catalog';
import { archiveRoot, categoryFromPath, collectMediaFacts, isMetadataFile, type MediaFacts } from '../instagramArchive/layout';
import { importArchiveMedia, summarize, type ImportResult } from '../instagramArchive/importer';
import { extractThumbnail, pickArchives, prepareForUpload } from '../instagramArchive/archiveIo';
import { openZip, readTextEntry, ZipError, type ZipEntry } from '../zip/reader';
import { DEFAULT_ZIP_LIMITS } from '../zip/limits';
import type { ByteSource } from '../zip/source';

// 9.18.10.4 · D-830 — "Import from Instagram": a single path in one modal,
// as Apple's modality guidance asks. Guide → choose the ZIP → read it on the
// device → review and select → add one at a time with a determinate progress
// bar and Stop → a summary. Nothing leaves the device but the items chosen.

type Props = {
  token: string;
  visible: boolean;
  /**
   * What is already on the profile: archive paths (shown as such, not
   * selectable) and how many more items it can hold. Loaded when the screen
   * opens and again after adding, so a second round starts from the truth.
   */
  loadExisting: () => Promise<{ keys: ReadonlySet<string>; slotsLeft: number }>;
  /** `changed` when anything was added, so the profile reloads its grid. */
  onClose: (changed: boolean) => void;
};

type Step =
  | { name: 'guide' }
  | { name: 'reading' }
  | { name: 'error'; message: string }
  | { name: 'review' }
  | { name: 'uploading'; index: number; total: number; stopping: boolean }
  | { name: 'done'; results: Map<string, ImportResult> };

const COLUMNS = 3;
const GAP = 2;
const CATEGORIES: ArchiveCategory[] = ['posts', 'reels', 'stories', 'igtv', 'archived', 'profile', 'other'];

/**
 * Reads the person's own activity JSON in every part and returns what it says
 * about each media file. A file that cannot be parsed is skipped: the folder
 * month still dates its media.
 */
export async function readArchiveFacts(parts: Array<{ source: ByteSource; entries: ZipEntry[] }>): Promise<Map<string, MediaFacts>> {
  const facts = new Map<string, MediaFacts>();
  for (const { source, entries } of parts) {
    const root = archiveRoot(entries.map((entry) => entry.name));
    for (const entry of entries) {
      const key = keyOf(entry.name, root);
      if (entry.directory || entry.unavailable || !isMetadataFile(key) || entry.uncompressedSize > DEFAULT_ZIP_LIMITS.maxTextBytes) continue;
      let json: unknown;
      try {
        json = JSON.parse(await readTextEntry(source, entry));
      } catch {
        continue;
      }
      for (const [path, fact] of collectMediaFacts(json, categoryFromPath(key))) {
        const before = facts.get(path);
        facts.set(path, {
          takenAt: before?.takenAt && fact.takenAt ? (before.takenAt < fact.takenAt ? before.takenAt : fact.takenAt) : before?.takenAt ?? fact.takenAt,
          category: before?.category ?? fact.category,
        });
      }
    }
  }
  return facts;
}

export function archiveErrorKey(error: unknown):
  'instagramImport.notZip' | 'instagramImport.untrusted' | 'instagramImport.tooBig' | 'instagramImport.unreadable' {
  const code = error instanceof ZipError ? error.code : null;
  if (code === 'not_a_zip') return 'instagramImport.notZip';
  if (code === 'unsafe_name' || code === 'duplicate_name' || code === 'bomb') return 'instagramImport.untrusted';
  if (code === 'too_large' || code === 'too_many_entries') return 'instagramImport.tooBig';
  return 'instagramImport.unreadable';
}

type Existing = { status: 'loading' | 'error' } | { status: 'ready'; keys: ReadonlySet<string>; slotsLeft: number };
const NONE: ReadonlySet<string> = new Set();

export default function InstagramArchiveImport({ token, visible, loadExisting, onClose }: Props) {
  const { t, lang } = useTranslation();
  const colors = useTheme();
  const insets = useSafeAreaInsets();
  const [step, setStep] = useState<Step>({ name: 'guide' });
  const [catalog, setCatalog] = useState<ArchiveCatalog | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [width, setWidth] = useState(0);
  const [filter, setFilter] = useState<ArchiveCategory | 'all'>('all');
  const sources = useRef<ByteSource[]>([]);
  const disposers = useRef<Array<() => void>>([]);
  const queue = useRef<ArchiveMedia[]>([]);
  const queued = useRef(new Set<string>());
  const busy = useRef(false);
  const signal = useRef({ aborted: false });
  const added = useRef(false);
  const count = (n: number) => formatDisplayCount(n, lang);
  const [existing, setExisting] = useState<Existing>({ status: 'loading' });
  const refreshExisting = useCallback(() => {
    setExisting({ status: 'loading' });
    loadExisting()
      .then(({ keys, slotsLeft }) => setExisting({ status: 'ready', keys, slotsLeft }))
      .catch(() => setExisting({ status: 'error' }));
    // `loadExisting` is a new function on each render of the profile.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { refreshExisting(); }, [refreshExisting]);
  const existingKeys = existing.status === 'ready' ? existing.keys : NONE;
  const slotsLeft = existing.status === 'ready' ? existing.slotsLeft : 0;
  // Literal keys, so the dictionary check sees every label in use.
  const categoryLabel = (category: ArchiveCategory | 'all') => ({
    all: t('instagramImport.catAll'),
    posts: t('instagramImport.catPosts'),
    reels: t('instagramImport.catReels'),
    stories: t('instagramImport.catStories'),
    igtv: t('instagramImport.catIgtv'),
    archived: t('instagramImport.catArchived'),
    profile: t('instagramImport.catProfile'),
    other: t('instagramImport.catOther'),
  })[category];

  const release = useCallback(() => {
    for (const dispose of disposers.current.splice(0)) dispose();
    for (const part of sources.current.splice(0)) part.close();
    queue.current = [];
    queued.current.clear();
  }, []);
  useEffect(() => release, [release]);

  const reset = () => {
    release();
    setCatalog(null);
    setSelected(new Set());
    setThumbs({});
    setFilter('all');
    setStep({ name: 'guide' });
  };

  const close = () => {
    if (step.name === 'uploading') { stop(); return; }
    const changed = added.current;
    reset();
    added.current = false;
    onClose(changed);
  };

  const choose = async () => {
    release();
    let picked: ByteSource[] = [];
    try {
      picked = await pickArchives();
    } catch {
      setStep({ name: 'error', message: t('instagramImport.unreadable') });
      return;
    }
    if (!picked.length) return;
    sources.current = picked;
    setStep({ name: 'reading' });
    try {
      const parts: Array<{ source: ByteSource; entries: ZipEntry[] }> = [];
      for (const part of picked) parts.push({ source: part, entries: (await openZip(part)).entries });
      const found = catalogArchive(parts.map((part) => part.entries), await readArchiveFacts(parts));
      setCatalog(found);
      setSelected(new Set());
      setFilter('all');
      setStep(found.media.length ? { name: 'review' } : { name: 'error', message: t('instagramImport.noMedia') });
    } catch (error) {
      release();
      setStep({ name: 'error', message: t(archiveErrorKey(error)) });
    }
  };

  // Thumbnails are made one at a time and only for tiles that came into view,
  // so a large archive is never unpacked just to be looked at.
  const pump = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    try {
      while (queue.current.length && sources.current.length) {
        const item = queue.current.shift()!;
        const part = sources.current[item.source];
        if (!part) continue;
        try {
          const thumb = await extractThumbnail(part, item);
          disposers.current.push(thumb.dispose);
          setThumbs((current) => ({ ...current, [item.key]: thumb.uri }));
        } catch {
          // A tile without a picture is still selectable; the upload checks the file again.
        }
      }
    } finally {
      busy.current = false;
    }
  }, []);
  const onViewable = useRef(({ viewableItems }: { viewableItems: Array<{ item: ArchiveMedia }> }) => {
    for (const { item } of viewableItems) {
      if (item.kind !== 'image' || item.tooLarge || queued.current.has(item.key)) continue;
      queued.current.add(item.key);
      queue.current.push(item);
    }
    void pump();
  }).current;

  const shown = useMemo(
    () => (catalog?.media ?? []).filter((item) => filter === 'all' || item.category === filter),
    [catalog, filter],
  );
  const present = useMemo(() => {
    const counts = new Map<ArchiveCategory, number>();
    for (const item of catalog?.media ?? []) counts.set(item.category, (counts.get(item.category) ?? 0) + 1);
    return CATEGORIES.filter((category) => counts.has(category)).map((category) => ({ category, count: counts.get(category)! }));
  }, [catalog]);
  // "Select all" adds what is in view, after what is already chosen, up to the free places.
  const selectable = useMemo(
    () => shown.filter((item) => !item.tooLarge && !existingKeys.has(item.key)),
    [shown, existingKeys],
  );
  const toggle = (item: ArchiveMedia) => {
    if (item.tooLarge || existingKeys.has(item.key)) return;
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(item.key)) next.delete(item.key);
      else if (next.size < slotsLeft) next.add(item.key);
      return next;
    });
  };
  const selectAll = () => setSelected((current) => {
    const next = new Set(current);
    for (const item of selectable) { if (next.size >= Math.max(0, slotsLeft)) break; next.add(item.key); }
    return next;
  });

  const run = async (items: ArchiveMedia[]) => {
    if (!sources.current.length || !items.length) return;
    const parts = sources.current;
    signal.current = { aborted: false };
    setStep({ name: 'uploading', index: 0, total: items.length, stopping: false });
    const results = await importArchiveMedia(items, {
      prepare: (item, abort) => prepareForUpload(parts[item.source], item, abort),
      requestTicket: async (input) => {
        const ticket = await api.createMediaTicket(token, { ...input, purpose: 'profile' });
        return { uploadUrl: ticket.upload_url, url: ticket.url };
      },
      finalize: (input) => api.finalizeMedia(token, input),
      attach: async (input) => { await api.attachProfileMedia(token, input); added.current = true; },
    }, {
      signal: signal.current,
      onProgress: ({ index, total }) => setStep((current) => ({
        name: 'uploading', index, total, stopping: current.name === 'uploading' ? current.stopping : false,
      })),
    });
    setStep({ name: 'done', results });
    // What was added is on the profile now; a retry or a second round must know.
    if ([...results.values()].includes('added')) refreshExisting();
  };
  const stop = () => {
    signal.current.aborted = true;
    setStep((current) => current.name === 'uploading' ? { ...current, stopping: true } : current);
  };

  const tileSize = width > 0 ? Math.floor((width - GAP * (COLUMNS - 1)) / COLUMNS) : 0;
  const renderTile = ({ item, index }: { item: ArchiveMedia; index: number }) => {
    const onProfile = existingKeys.has(item.key);
    const disabled = onProfile || item.tooLarge;
    const isSelected = selected.has(item.key);
    const lastColumn = index % COLUMNS === COLUMNS - 1;
    const thumb = thumbs[item.key];
    const total = shown.length;
    const label = t(item.kind === 'video' ? 'profile.mediaVideoLabel' : 'profile.mediaPhotoLabel', { n: count(index + 1), total: count(total) })
      + (onProfile ? `, ${t('instagramImport.alreadyOnProfile')}` : item.tooLarge ? `, ${t('instagramImport.tooLarge')}` : '');
    return (
      <TouchableOpacity testID={`import-tile-${index}`} accessibilityRole="checkbox" accessibilityLabel={label}
        accessibilityState={{ checked: isSelected, disabled }} disabled={disabled} activeOpacity={0.8}
        onPress={() => toggle(item)}
        style={{ width: lastColumn ? width - (COLUMNS - 1) * (tileSize + GAP) : tileSize, height: tileSize,
          marginRight: lastColumn ? 0 : GAP, marginBottom: GAP }}>
        <View style={[styles.fill, { backgroundColor: item.kind === 'video' ? '#1C1C1E' : colors.gray100 }]}>
          {thumb ? <Image source={{ uri: thumb }} style={styles.fill} resizeMode="cover" accessible={false} /> : null}
          {item.kind === 'video' ? <View style={styles.center} pointerEvents="none">
            <Ionicons name="play" size={26} color="#fff" accessible={false} /></View> : null}
          {disabled ? <View style={styles.dim} pointerEvents="none">
            <Text style={styles.dimText}>{onProfile ? t('instagramImport.alreadyOnProfile') : t('instagramImport.tooLarge')}</Text>
          </View> : null}
          {/* A white disc under the selected mark keeps its tick white on any photo, as in Photos. */}
          {!disabled ? <View style={[styles.check, isSelected && styles.checkOn]} pointerEvents="none">
            <Ionicons name={isSelected ? 'checkmark-circle' : 'ellipse-outline'} size={24}
              color={isSelected ? '#2878d4' : '#fff'} accessible={false} />
          </View> : null}
        </View>
      </TouchableOpacity>
    );
  };

  let body: React.ReactNode;
  if (step.name === 'guide' || step.name === 'error') {
    body = (
      <ScrollView contentContainerStyle={styles.guide}>
        {step.name === 'error'
          ? <Text testID="import-error" style={[styles.error, { color: colors.black }]}>{step.message}</Text>
          : null}
        <Text style={[styles.lead, { color: colors.black }]}>{t('instagramImport.guideIntro')}</Text>
        {[t('instagramImport.guideStep1'), t('instagramImport.guideStep2'), t('instagramImport.guideStep3')].map((text, index) => (
          <View key={index} style={styles.stepRow}>
            <Text style={[styles.stepNumber, { color: colors.gray600 }]}>{count(index + 1)}</Text>
            <Text style={[styles.stepText, { color: colors.black }]}>{text}</Text>
          </View>
        ))}
        <Text style={[styles.note, { color: colors.gray600 }]}>{t('instagramImport.guideOwnOnly')}</Text>
        <Text style={[styles.note, { color: colors.gray600 }]}>{t('instagramImport.guideParts')}</Text>
        <Text style={[styles.note, { color: colors.gray600 }]}>{t('instagramImport.guideOnDevice')}</Text>
        {existing.status === 'error' ? (
          <View style={styles.existingError}>
            <Text testID="import-existing-error" style={[styles.note, { color: colors.black }]}>{t('profile.mediaUnavailable')}</Text>
            <TouchableOpacity testID="import-existing-retry" accessibilityRole="button" onPress={refreshExisting} style={styles.textButton}>
              <Text style={[styles.textButtonLabel, { color: '#2878d4' }]}>{t('common.retry')}</Text>
            </TouchableOpacity>
          </View>
        ) : null}
        <PrimaryActionButton testID="import-choose" style={styles.primary} disabled={existing.status !== 'ready'}
          loading={existing.status === 'loading'}
          label={step.name === 'error' ? t('instagramImport.chooseAnother') : t('instagramImport.choose')} onPress={() => void choose()} />
      </ScrollView>
    );
  } else if (step.name === 'reading') {
    body = (
      <View style={styles.centerPage} testID="import-reading">
        <ActivityIndicator accessibilityLabel={t('instagramImport.reading')} />
        <Text style={[styles.note, { color: colors.gray600 }]}>{t('instagramImport.reading')}</Text>
      </View>
    );
  } else if (step.name === 'review') {
    body = (
      <View style={styles.flex}>
        <View style={styles.reviewHeader}>
          <Text testID="import-found" style={[styles.lead, { color: colors.black }]}>
            {t('instagramImport.found', { count: count(catalog?.media.length ?? 0) })}</Text>
          <Text style={[styles.note, { color: colors.gray600 }]}>{t('instagramImport.slotsLeft', { count: count(Math.max(0, slotsLeft)) })}</Text>
          <View style={styles.reviewActions}>
            <TouchableOpacity testID="import-select-all" accessibilityRole="button" onPress={selectAll} style={styles.textButton}>
              <Text style={[styles.textButtonLabel, { color: '#2878d4' }]}>{t('instagramImport.selectAll')}</Text>
            </TouchableOpacity>
            <TouchableOpacity testID="import-select-none" accessibilityRole="button" onPress={() => setSelected(new Set())} style={styles.textButton}>
              <Text style={[styles.textButtonLabel, { color: '#2878d4' }]}>{t('instagramImport.selectNone')}</Text>
            </TouchableOpacity>
          </View>
          {present.length > 1 ? <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.chips}>
            {[{ category: 'all' as const, count: catalog?.media.length ?? 0 }, ...present].map(({ category, count: n }) => {
              const active = filter === category;
              return (
                <TouchableOpacity key={category} testID={`import-filter-${category}`} accessibilityRole="button"
                  accessibilityState={{ selected: active }} onPress={() => setFilter(category)}
                  style={[styles.chip, { borderColor: active ? colors.black : colors.gray200, backgroundColor: active ? colors.black : 'transparent' }]}>
                  <Text style={[styles.chipText, { color: active ? colors.white : colors.black }]}>
                    {`${categoryLabel(category)} ${count(n)}`}</Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView> : null}
        </View>
        <View style={styles.flex} onLayout={(event) => setWidth(event.nativeEvent.layout.width)}>
          {width > 0 ? <FlatList testID="import-grid" data={shown} keyExtractor={(item) => item.key}
            numColumns={COLUMNS} renderItem={renderTile} onViewableItemsChanged={onViewable}
            viewabilityConfig={{ itemVisiblePercentThreshold: 10 }} /> : null}
        </View>
        <View style={[styles.footer, { paddingBottom: Math.max(insets.bottom, 12) }]}>
          <PrimaryActionButton testID="import-add" disabled={selected.size === 0}
            label={t('instagramImport.add', { count: count(selected.size) })}
            onPress={() => void run((catalog?.media ?? []).filter((item) => selected.has(item.key)))} />
        </View>
      </View>
    );
  } else if (step.name === 'uploading') {
    const fraction = step.total ? step.index / step.total : 0;
    body = (
      <View style={styles.centerPage} testID="import-uploading">
        <Text style={[styles.lead, { color: colors.black }]}>
          {t('instagramImport.adding', { n: count(step.index + 1), total: count(step.total) })}</Text>
        <View style={[styles.track, { backgroundColor: colors.gray200 }]} accessibilityRole="progressbar"
          accessibilityValue={{ min: 0, max: step.total, now: step.index }}>
          <View testID="import-progress" style={[styles.bar, { width: `${Math.round(fraction * 100)}%` }]} />
        </View>
        {step.stopping
          ? <Text style={[styles.note, { color: colors.gray600 }]}>{t('instagramImport.stopping')}</Text>
          : <TouchableOpacity testID="import-stop" accessibilityRole="button" onPress={stop} style={styles.textButton}>
              <Text style={[styles.textButtonLabel, { color: colors.black }]}>{t('instagramImport.stop')}</Text>
            </TouchableOpacity>}
      </View>
    );
  } else {
    const counts = summarize(step.results);
    const failed = (catalog?.media ?? []).filter((item) => step.results.get(item.key) === 'failed');
    body = (
      <ScrollView contentContainerStyle={styles.guide} testID="import-done">
        {counts.added ? <Text style={[styles.lead, { color: colors.black }]}>{t('instagramImport.doneAdded', { count: count(counts.added) })}</Text> : null}
        {counts.duplicate ? <Text style={[styles.note, { color: colors.gray600 }]}>{t('instagramImport.doneDuplicate', { count: count(counts.duplicate) })}</Text> : null}
        {counts.too_large + counts.cancelled ? <Text style={[styles.note, { color: colors.gray600 }]}>
          {t('instagramImport.doneSkipped', { count: count(counts.too_large + counts.cancelled) })}</Text> : null}
        {counts.failed ? <Text testID="import-failed" style={[styles.note, { color: colors.black }]}>{t('instagramImport.doneFailed', { count: count(counts.failed) })}</Text> : null}
        {counts.limit ? <Text style={[styles.note, { color: colors.black }]}>{t('instagramImport.profileFull')}</Text> : null}
        {failed.length ? <TouchableOpacity testID="import-retry" accessibilityRole="button" onPress={() => void run(failed)} style={styles.textButton}>
          <Text style={[styles.textButtonLabel, { color: '#2878d4' }]}>{t('instagramImport.retryFailed')}</Text>
        </TouchableOpacity> : null}
        <PrimaryActionButton testID="import-finish" style={styles.primary} label={t('common.done')} onPress={close} />
      </ScrollView>
    );
  }

  return (
    <Modal visible={visible} animationType="slide" onRequestClose={close}>
      {/* PageHeader's ScreenHeader already keeps clear of the status bar. */}
      <View testID="instagram-archive-import" style={[styles.page, { backgroundColor: colors.white }]}>
        <PageHeader title={t('instagramImport.title')} onBack={close} backTestID="import-close" />
        {body}
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  page: { flex: 1 },
  flex: { flex: 1 },
  guide: { padding: 16, gap: 12 },
  lead: { fontSize: 17, lineHeight: 23 },
  note: { fontSize: 15, lineHeight: 21 },
  error: { fontSize: 16, lineHeight: 22, fontWeight: '600' },
  stepRow: { flexDirection: 'row', gap: 10 },
  stepNumber: { fontSize: 16, width: 18, textAlign: 'right' },
  stepText: { flex: 1, fontSize: 16, lineHeight: 22 },
  primary: { marginTop: 12 },
  centerPage: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 16, paddingHorizontal: 24 },
  reviewHeader: { paddingHorizontal: 16, paddingBottom: 8, gap: 4 },
  reviewActions: { flexDirection: 'row', gap: 16 },
  chips: { gap: 8, paddingVertical: 6 },
  chip: { minHeight: 36, paddingHorizontal: 14, borderRadius: 18, borderWidth: 1, justifyContent: 'center' },
  chipText: { fontSize: 14 },
  existingError: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  textButton: { minHeight: 44, justifyContent: 'center' },
  textButtonLabel: { fontSize: 16 },
  fill: { width: '100%', height: '100%' },
  center: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, alignItems: 'center', justifyContent: 'center' },
  dim: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.55)', alignItems: 'center', justifyContent: 'center', padding: 6 },
  dimText: { color: '#fff', fontSize: 12, fontWeight: '600', textAlign: 'center' },
  check: { position: 'absolute', top: 6, right: 6 },
  checkOn: { backgroundColor: '#fff', borderRadius: 12 },
  footer: { paddingHorizontal: 16, paddingTop: 12 },
  track: { width: '100%', height: 6, borderRadius: 3, overflow: 'hidden' },
  bar: { height: '100%', backgroundColor: '#2878d4' },
});
